import EventSender from './EventSender';
import SessionManager from './utilities/SessionManager';
import SequenceNumberHandler from './utilities/SequenceNumberHandler';
import IItems from './types/IItems';
import DesignSourceEnum from './enums/DesignSourceEnum';
import ICustomData from './types/ICustomData';
import GAScriptLoader from './utilities/GAScriptLoader';
import DesignInteractionEnum from './enums/DesignInteractionEnum';
import PlatformEnum from './enums/PlatformEnum';
import IAppConnect from './types/IAppConnect';
import ModuleClient from './ModuleClient';
import IModuleClient from './types/IModuleClient';
import IUserIds from './types/IUserIds';
import ArticleActionEnum from './enums/ArticleActionEnum';
import CookieReader from './utilities/CookieReader';
import MetadataManager from './utilities/MetadataManager';
import DesignSaveModeEnum from './enums/DesignSaveModeEnum';

/**
 * Entrypoint API class for applications.
 */
export default class InsightsApi {
	// eslint-disable-next-line
	private _isEnabled = false;
	private eventSender = new EventSender();
	private session = new SessionManager();
	private sequenceNumber = new SequenceNumberHandler();

	/**
	 *	Returns a boolean that indicates whether or not insights api is enabled.
	 */
	public get isEnabled(): boolean {
		return this._isEnabled;
	}

	/**
	 * Connect the component for an application.
	 *
	 * @param props Initialization data for the application.
	 */
	public async connectApplication(props: IAppConnect): Promise<void> {
		if (typeof props.applicationId !== 'string') {
			throw new Error(
				'Property applicationId is not a string. DPC Initialization will not proceed.'
			);
		}
		if (!Object.values(PlatformEnum).includes(props.platform)) {
			throw Error(
				`Platform ${props.platform} is not valid. Check PlatformEnum. DPC Initialization will not proceed.`
			);
		}
		if (props.kiosk) {
			props.platform = PlatformEnum.kiosk;
		}
		MetadataManager.registerApplication(props);
		if (props.enableGa === true) {
			const cookieReader = new CookieReader();
			try {
				cookieReader.getGaId();
				const scriptLoader = new GAScriptLoader();
				await scriptLoader.loadGoogleAnalyticsScript();
				await this.eventSender.setupAndSendInit(props);
			} catch (error) {
				// Disable GA in case we cannot find the cookie.
				// eslint-disable-next-line
				console.warn(`GA data not found. Disabling GA events.`);
				MetadataManager.updateApplication({ enableGa: false });
				// Send regular ApplicationStartUpEvents instead.
				await this.eventSender.sendApplicationStartUpEvents();
			}
		} else {
			await this.eventSender.sendApplicationStartUpEvents();
		}
		this._isEnabled = true;
	}

	/**
	 * Receive module initialization data and
	 * returns instance of module client.
	 *
	 * @param moduleMetadata Initialization data for the module.
	 * @returns The module client instance.
	 */
	public moduleClient(moduleMetadata: IModuleClient): ModuleClient {
		if (typeof moduleMetadata.moduleId !== 'string') {
			throw new Error(
				'Property moduleId is not a string. Module client initialization will not proceed.'
			);
		}
		if (!this.isEnabled) {
			throw new Error('Application is not enabled. Module client initialization will not proceed.');
		}
		const applicationMetadata = MetadataManager.getApplicationMetadata();
		if (!applicationMetadata || Object.keys(applicationMetadata).length === 0) {
			throw new Error(
				'Application metadata not found. Module client initialization will not proceed.'
			);
		}

		return new ModuleClient(moduleMetadata, this);
	}

	/**
	 * Disable the component. Will reset session data and metadata, as well as the sequence number.
	 */
	public disconnect(): void {
		this._isEnabled = false;
		this.session.clearSessions();
		this.sequenceNumber.resetSequenceNumber();
		MetadataManager.clear();
	}

	/**
	 * Send an addToCart event, if the component is enabled.
	 *
	 * @param data The included items in the cart.
	 */
	public addToCart(data: IItems): void {
		if (this.isEnabled) {
			this.eventSender.sendAddToCart(data);
		}
	}

	/**
	 * Send an addToWishList event, if the component is enabled.
	 *
	 * @param data The included items in the wish list.
	 */
	public addToWishList(data: IItems): void {
		if (this.isEnabled) {
			this.eventSender.sendAddToWishList(data);
		}
	}

	/**
	 * Send an initPlanningSession event, if the component is enabled.
	 *
	 * @param designId The design id that started the session.
	 * @param source The source of the planning session.
	 * @param [payload] Optional payload data.
	 * @param [spr] Optional The SPR that was used as base for the planning if any.
	 *
	 */
	public initPlanningSession(
		designId: string,
		source: DesignSourceEnum,
		payload?: {},
		spr?: string
	): void {
		if (this.isEnabled) {
			this.eventSender.sendInitPlanningSession(designId, source, payload, spr);
		}
	}

	/**
	 * Send a savePlanner event, if the component is enabled.
	 *
	 * @param designId The design id to save, optional.
	 * @param [payload] Optional payload data.
	 * @param [mode] How this design was saved or shared.
	 */
	public saveDesign(designId?: string, payload?: {}, mode?: DesignSaveModeEnum): void {
		if (this.isEnabled) {
			this.eventSender.sendSavePlanner(designId, payload, mode);
		}
	}

	/**
	 * Send a custom event, if the component is enabled.
	 *
	 * @param data The information about the custom event.
	 */
	public sendEvent(data: ICustomData): void {
		if (this.isEnabled) {
			this.eventSender.sendCustomEvent(data);
		}
	}

	/**
	 * Send a design interaction event, if the component is enabled.
	 *
	 * @param designId The design id which was interacted with.
	 * @param interaction The interaction source.
	 * @param [payload] Optional payload data.
	 */
	public sendDesignInteractionEvent(
		designId: string,
		interaction: DesignInteractionEnum,
		payload?: {}
	): void {
		if (this.isEnabled) {
			this.eventSender.sendDesignInteractionEvent(designId, interaction, payload);
		}
	}

	/**
	 * Send a article action event, if the component is enabled.
	 *
	 * @param articleId The article id.
	 * @param action The action being performed.
	 * @param isGlobal Indicate if article id is global or local.
	 * @param [payload] Optional payload data.
	 */
	public articleAction(
		articleId: string,
		action: ArticleActionEnum,
		isGlobal: boolean,
		payload?: {}
	): void {
		if (this.isEnabled) {
			this.eventSender.sendArticleAction(articleId, action, isGlobal, payload);
		}
	}

	/**
	 * Sets the A/B version for the session to facilitate A/B testing, if the component is enabled.
	 *
	 * @param abVersionName Name of the ab version.
	 * @param abVersionVariation Optional ab variation.
	 */
	public setAbVersion(abVersionName: string, abVersionVariation?: string): void {
		if (this.isEnabled) {
			MetadataManager.updateApplication({ abVersionName, abVersionVariation });
			this.eventSender.sendSetAbVersion();
		}
	}

	/**
	 * Send a flow step event, if the component is enabled.
	 *
	 * @param flowName Name of the flow.
	 * @param stepName Name of the step in the flow.
	 * @param stepLevel Level of step in the flow.
	 */
	public sendUserFlowStep(
		flowName: string,
		stepName: string,
		stepLevel: number,
		payload?: {}
	): void {
		if (this.isEnabled) {
			this.eventSender.sendUserFlowStep(flowName, stepName, stepLevel, payload);
		}
	}

	/**
	 * Send a flow step event, if the component is enabled.
	 * Asynchronous version of this function.
	 *
	 * @param flowName Name of the flow.
	 * @param stepName Name of the step in the flow.
	 * @param stepLevel Level of step in the flow.
	 */
	public async sendUserFlowStepAsync(
		flowName: string,
		stepName: string,
		stepLevel: number,
		payload?: {}
	): Promise<void> {
		if (this.isEnabled) {
			await this.eventSender.sendUserFlowStep(flowName, stepName, stepLevel, payload);
		}
	}

	/**
	 * Get user ids.
	 *
	 * @returns The user ids (sessionId, planningId, longTermId and ipexId).
	 */
	public getIds(): IUserIds | undefined {
		if (this.isEnabled) {
			return this.session.getIds();
		}
	}
}
